/** @prettier */
import { List } from 'immutable';
import PropTypes from 'prop-types';
import React, { useContext, useMemo, useState } from 'react';
import { connect } from 'react-redux';

import RadioButton, { RadioButtonOption } from '@xanadu/ui-shared/components/widgets/RadioButton';
import ChildrenPropsContext from '@xanadu/ui-shared/contexts/ChildrenPropsContext';
import { useImprovedTranslation } from '@xanadu/ui-shared/hooks';
import { isFuturesOrPredictionsProduct } from '@xanadu/ui-shared/modules/application/selectors';
import { setOddsType as setOddsTypeAct } from '@xanadu/ui-shared/modules/user/actions';
import fetchService from '@xanadu/ui-shared/utils/fetchService';

import styles from './OddsTypeSelector.module.scss';

const OddsTypeSelector = ({
  oddsType,
  oddsTypes,
  setOddsType,
  userIsLoggedIn = false,
  isFuturesOrPredictions = false,
}) => {
  const { availableOddsTypes = [] } = useContext(ChildrenPropsContext) || {};

  const filteredOddsTypes = useMemo(() => {
    let viableTypes = availableOddsTypes;
    if (!isFuturesOrPredictions) {
      viableTypes = availableOddsTypes.filter((oddsT) => oddsT !== 'PROBABILITY');
    }
    return oddsTypes.filter(({ active, id }) => active && viableTypes.includes(id));
  }, [isFuturesOrPredictions, availableOddsTypes]);

  const [updating, setUpdating] = useState(false);

  const { t } = useImprovedTranslation();

  const loggedInOddsTypeEdit = (value) => async () => {
    try {
      fetchService(
        '/bpapi/rest/account',
        {
          method: 'PUT',
          body: JSON.stringify({
            'odds-type-id': value,
          }),
        },
        { camelizeKeys: false }
      ).then((accountResponse) => {
        setOddsType(accountResponse['odds-type']);
        setUpdating(false);
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Error updating odds account settings', e.message);
      setUpdating(false);
    }
  };

  const onPropertyChange = (value) => {
    if (userIsLoggedIn) {
      const { id: oddsTypeId } = oddsTypes.find(({ id }) => id === value) || {};
      setUpdating(true);
      loggedInOddsTypeEdit(oddsTypeId)();
    } else {
      setOddsType(value);
    }
  };

  if (filteredOddsTypes.size <= 1) {
    return null;
  }

  return (
    <div className={styles.main}>
      <div className={styles.label}>{t('Odds')}</div>
      <RadioButton
        onChange={onPropertyChange}
        value={oddsType}
        fluid
        type="solid"
        className={styles.radioButton}
      >
        {filteredOddsTypes.map((oddsTypeItem) => (
          <RadioButtonOption
            key={oddsTypeItem.id}
            label={oddsTypeItem.name}
            value={oddsTypeItem.id}
            name="odds-type"
            disabled={updating}
            className={styles.radioButtonOption}
          />
        ))}
      </RadioButton>
    </div>
  );
};

OddsTypeSelector.propTypes = {
  oddsType: PropTypes.string.isRequired,
  oddsTypes: PropTypes.instanceOf(List).isRequired,
  setOddsType: PropTypes.func.isRequired,
  userIsLoggedIn: PropTypes.bool,
  isFuturesOrPredictions: PropTypes.bool,
};

const getSelectedOddsType = (state) => {
  const selectedItem = state.getIn(['user', 'oddsType']);
  const item = state.getIn(['lookups', 'oddsTypes']).find((value) => selectedItem === value.id);
  return item ? item.id : '';
};

const mapDispatchToProps = {
  setOddsType: setOddsTypeAct,
  // updateUserSitePreferences: updateUserSitePreferencesAct,
};

function mapStateToProps(state) {
  return {
    oddsType: getSelectedOddsType(state),
    oddsTypes: state.getIn(['lookups', 'oddsTypes']),
    userIsLoggedIn: state.getIn(['user', 'isLoggedIn'], false),
    isFuturesOrPredictions: isFuturesOrPredictionsProduct(state),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(OddsTypeSelector);
